import { useMemo } from 'react';
import classnames from 'classnames';

import MessageRenderer from '@/components/Feed/ChatMessage/MessageRenderer';
import ModerationActions from '@/components/Feed/ChatMessage/ModerationActions';
import AvatarLayered from '@/components/AvatarLayered';
import Reactions from '@/components/Feed/Reactions';
import IsAdmin from '@/components/UserLogic/IsAdmin';
import DisplayNameWithBadges from '@/components/DisplayNameWithBadges';
import LocationLink from '@/components/Feed/ChatMessage/LocationLink';
import { ChatMessage as ChatMessageI } from '@/models/feed';
import { toggleMessageReaction } from '@/state/features/feed';
import { openProfile } from '@/state/features/app';
import { useAppDispatch, useAppSelector } from '@/state/store';
import { sendEvent } from '@/utils/analytics';
import { useCurrentUserIsGuest } from '@/hooks/users';

import * as styles from './styles.module.scss';

interface ChatMessageProps {
    message: ChatMessageI;
}

export default function ChatMessage({ message }: ChatMessageProps) {
    const roomId = useAppSelector((state) => state.feed.roomId);
    const currentUserProfile = useAppSelector((state) => state.profile.currentUserProfile);
    const dispatch = useAppDispatch();
    const isGuest = useCurrentUserIsGuest();

    const profile = message.profile;

    const isGlobalChatMessage = message.environmentID !== roomId;

    function handleReactionSubmit(reaction: string) {
        dispatch(toggleMessageReaction([message.id, reaction, message.environmentID]));
        sendEvent('toggle_reaction', { reaction });
    }

    const humanTimestamp = useMemo(() => {
        const date = new Date(message.timestamp * 1000);
        return date.toLocaleString();
    }, [message.timestamp]);

    const isVisible = !!profile;

    const containsMentionForCurrentUser = message.mentions?.some(
        (profile) => profile.userID === currentUserProfile?.userID,
    );

    return (
        <div>
            <div
                className={classnames('text-[14px] relative', styles.ChatMessage, {
                    [styles.visible]: isVisible,
                    [styles.highlighted]: containsMentionForCurrentUser,
                    [styles.isGlobalChatMessage]: isGlobalChatMessage,
                })}
            >
                <AvatarLayered
                    classname={styles.avatar}
                    backgroundImagePath={profile.background?.thumbnailPath}
                    characterImagePath={profile.character?.thumbnailPath}
                    frameImagePath={profile.avatarFrame?.imagePath}
                    onClick={() => dispatch(openProfile(profile))}
                    title={`View ${profile.displayName}'s Profile`}
                />
                <div>
                    <DisplayNameWithBadges profile={profile} shouldLinkToProfile />
                    <div title={humanTimestamp} className={styles.messageText}>
                        <MessageRenderer message={message} />
                    </div>
                    <LocationLink locationId={message.environmentID} />
                    <div>
                        <Reactions
                            reactions={message.reactions}
                            onSubmitReaction={handleReactionSubmit}
                            controlsClassName={styles.reactionsMenu}
                            disabled={currentUserProfile?.isBanned || isGuest}
                            currentUserId={currentUserProfile?.userID}
                        />
                    </div>
                </div>
                <IsAdmin>
                    <ModerationActions
                        messageId={message.id}
                        userId={message.senderId}
                        userDisplayName={profile?.displayName}
                        userBannedUntil={profile?.bannedUntil}
                    />
                </IsAdmin>
            </div>
        </div>
    );
}
